// <pfs-user-presence>: kleiner Pill der grün leuchtet wenn der
// Ziel-User gerade online ist, grau sonst. Tenant-agnostisch.
//
// Datenquelle: der tenant-scoped REST-Shim aus pfs-auth
//   GET <presence-url>?external_user_ids=<csv>
//   → { "Presences": [ { "ExternalUserId", "Status", "LastSeenAt" } ] }
// (PFS-388, Pattern 9 — Browser-Composition, kein BFF-Aggregat).
//
// Live-Update bevorzugt via liveBus (generischer SSE-Push-Primitiv,
// PFS-397) wenn data-events-url gesetzt ist — direkte typisierte
// Subscription, kein document-CustomEvent-Umweg, kein Mount-Reihenfolge-
// Problem. Polling ist der Standalone-FALLBACK: greift wie PFS-389 ohne
// data-events-url; mit data-events-url nur noch wenn poll-interval > 0
// (Backup). poll-interval=0 + data-events-url = reiner Push, null Fetch.
// Polling pausiert bei verstecktem Tab (document.hidden); die liveBus-
// Subscription bleibt unabhaengig davon offen (Deltas gehen sonst verloren).
//
// Mehrere Indikatoren die im selben Microtask mounten (z.B. eine Admin-
// Tabelle mit N Usern) werden in EINEN Request gebündelt — pro Poll-Tick
// genauso. Sonst wären es N parallele Fetches pro Tick.
//
// Attribute (alle server-rendered, dokumentiert):
//   user-id              external_user_id des Ziel-Users (Pflicht)
//   data-presence-url    Poll-Endpoint (default "/users/presence")
//   poll-interval        ms zwischen Polls (default 30000; 0 = einmalig
//                        bzw. mit data-events-url: Polling aus)
//   data-label-online    aria-label wenn online (server-i18n, optional)
//   data-label-offline   aria-label wenn offline (server-i18n, optional)
//   data-events-url      SSE-Quelle fuer liveBus (optional). Gesetzt =
//                        Push-Pfad aktiv (Deltas + ggf. Hub-Snapshot).
//   data-event-online    SSE-Event-Name fuer "online" (default "user.online")
//   data-event-offline   SSE-Event-Name fuer "offline" (default "user.offline")
//   data-event-id-field  Feld im Event-Payload mit der external_user_id
//                        (default "ExternalUserId") — Filter auf user-id

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { apiFetch } from "../../store/api-fetch.js";
import { liveBus } from "../../store/live-bus.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";

// PULSE_DURATION_MS muss zur @keyframes-Gesamtdauer unten passen — der
// Timer entfernt `just-flipped` am Animationsende, damit ein erneuter
// Flip die Keyframes neu starten kann.
const PULSE_DURATION_MS = 700;

const DEFAULT_PRESENCE_URL = "/users/presence";
const DEFAULT_POLL_INTERVAL_MS = 30_000;
// Untergrenze gegen versehentliches Hämmern bei zu kleinem Attribut.
const MIN_POLL_INTERVAL_MS = 5_000;

const STATUS_ONLINE = "online";

const DEFAULT_EVENT_ONLINE = "user.online";
const DEFAULT_EVENT_OFFLINE = "user.offline";
const DEFAULT_EVENT_ID_FIELD = "ExternalUserId";

// MUSS vor defineComponent declared sein — customElements.define
// upgrade'd Light-DOM-Hosts synchron, render() läuft bevor `var`-hoisted
// const-bodies assigned sind. Sonst: leerer <style>-Tag (siehe
// define-component.ts Kommentar).
const STYLES = `
${SHADOW_RESET_CSS}
:host {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: var(--pfs-color-presence-offline); /* D2: eigenes Presence-Token */
  vertical-align: middle;
  /* PFS-404: kein externes Margin — env-agnostic. Die WC bringt nur
     ihr Sizing (:host) mit; Abstand zu Geschwister-Content ist Sache
     des Konsumenten-Layouts (flex gap / eigenes Margin). */
  transition: background-color 120ms ease-out;
}
:host([online]) {
  background: var(--pfs-color-presence-online); /* D2: eigenes Presence-Token */
}
:host([online][just-flipped]) {
  animation: pfs-presence-pulse 700ms ease-out 1;
}
@keyframes pfs-presence-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  50% {
    transform: scale(1.6);
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}
`;

interface PresenceEntry {
  ExternalUserId: string;
  Status: string;
  LastSeenAt?: string;
}

interface PresenceResponse {
  Presences?: PresenceEntry[];
}

// Batch-Coalescing pro Presence-URL. Jeder Waiter registriert seine
// user-id; der erste Waiter pro Microtask schedult den Flush. Ein Flush
// = ein GET für alle bis dahin registrierten ids dieser URL.
type Waiter = (entry: PresenceEntry | null) => void;

interface UrlBatch {
  waiters: Map<string, Waiter[]>;
  scheduled: boolean;
}

const batches = new Map<string, UrlBatch>();

function requestPresence(url: string, userId: string): Promise<PresenceEntry | null> {
  return new Promise((resolve) => {
    let batch = batches.get(url);
    if (!batch) {
      batch = { waiters: new Map(), scheduled: false };
      batches.set(url, batch);
    }
    const list = batch.waiters.get(userId);
    if (list) {
      list.push(resolve);
    } else {
      batch.waiters.set(userId, [resolve]);
    }
    if (!batch.scheduled) {
      batch.scheduled = true;
      queueMicrotask(() => void flushBatch(url));
    }
  });
}

async function flushBatch(url: string): Promise<void> {
  const batch = batches.get(url);
  if (!batch) return;
  const snapshot = batch.waiters;
  batch.waiters = new Map();
  batch.scheduled = false;
  if (snapshot.size === 0) return;

  const ids = Array.from(snapshot.keys());
  const settle = (resolver: (id: string) => PresenceEntry | null) => {
    for (const [id, list] of snapshot) {
      const entry = resolver(id);
      for (const w of list) w(entry);
    }
  };

  try {
    const query = encodeURIComponent(ids.join(","));
    const data = await apiFetch<PresenceResponse>(`${url}?external_user_ids=${query}`);
    const byId = new Map<string, PresenceEntry>();
    for (const p of data.Presences ?? []) byId.set(p.ExternalUserId, p);
    settle((id) => byId.get(id) ?? null);
  } catch {
    // 401 / Netzwerk / fremd-Tenant — alles wird als offline behandelt
    // (der Endpoint leakt selbst keine Existenz, hier kein Sonderfall).
    settle(() => null);
  }
}

defineComponent({
  tagName: "pfs-user-presence",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = STYLES;
    ctx.shadowRoot.appendChild(style);

    const userId = host.getAttribute("user-id");
    if (!userId) {
      // Ohne Ziel-User bleibt der Pill grau (default) — kein Fetch.
      return;
    }

    const presenceUrl =
      host.getAttribute("data-presence-url")?.trim() || DEFAULT_PRESENCE_URL;
    const pollInterval = parsePollInterval(host.getAttribute("poll-interval"));

    const eventsUrl = host.getAttribute("data-events-url")?.trim() || "";
    const onlineEvent =
      host.getAttribute("data-event-online")?.trim() || DEFAULT_EVENT_ONLINE;
    const offlineEvent =
      host.getAttribute("data-event-offline")?.trim() || DEFAULT_EVENT_OFFLINE;
    const idField =
      host.getAttribute("data-event-id-field")?.trim() || DEFAULT_EVENT_ID_FIELD;
    // Poll-Fallback: ohne data-events-url wie PFS-389. Mit data-events-url
    // nur Backup wenn poll-interval > 0; =0 → reiner Push.
    const pollEnabled = !eventsUrl || pollInterval > 0;

    let pulseTimer: ReturnType<typeof setTimeout> | null = null;
    let intervalTimer: ReturnType<typeof setInterval> | null = null;
    let disposed = false;
    // PFS-405: der ERSTE angewandte Wert (Cold-Load, Re-Mount-Replay,
    // IV-prime-Snapshot, erster Poll) seedet den Zustand OHNE Pulse.
    // Nur ein echter Status-Wechsel DANACH pulst — sonst pulsiert der
    // Punkt bei jedem HTMX-Routing (Re-Mount + liveBus-Replay).
    let seeded = false;

    const applyAriaLabel = (online: boolean) => {
      const label = host.getAttribute(
        online ? "data-label-online" : "data-label-offline",
      );
      if (label) {
        host.setAttribute("role", "img");
        host.setAttribute("aria-label", label);
      }
    };

    const triggerPulse = () => {
      // Keyframes neu anstoßen: Attribut entfernen, Reflow erzwingen,
      // wieder setzen — sonst batcht der Browser beides zu einem No-Op.
      host.removeAttribute("just-flipped");
      void host.offsetWidth;
      host.setAttribute("just-flipped", "");
      if (pulseTimer !== null) clearTimeout(pulseTimer);
      pulseTimer = setTimeout(() => {
        host.removeAttribute("just-flipped");
        pulseTimer = null;
      }, PULSE_DURATION_MS);
    };

    const setOnline = (online: boolean) => {
      const wasOnline = host.hasAttribute("online");
      if (online) {
        host.setAttribute("online", "");
        // Pulse NUR bei einem echten Live-Wechsel grau→grün NACH dem
        // Seed — nicht beim ersten Wert (Cold-Load / Re-Mount-Replay /
        // Snapshot / erster Poll), sonst pulst es bei jedem Routing.
        if (seeded && !wasOnline) triggerPulse();
      } else {
        host.removeAttribute("online");
        host.removeAttribute("just-flipped");
        if (pulseTimer !== null) {
          clearTimeout(pulseTimer);
          pulseTimer = null;
        }
      }
      applyAriaLabel(online);
      seeded = true;
    };

    const poll = () => {
      void requestPresence(presenceUrl, userId).then((entry) => {
        if (disposed) return;
        setOnline(entry?.Status === STATUS_ONLINE);
      });
    };

    const startInterval = () => {
      if (intervalTimer !== null || pollInterval <= 0) return;
      intervalTimer = setInterval(poll, pollInterval);
    };
    const stopInterval = () => {
      if (intervalTimer !== null) {
        clearInterval(intervalTimer);
        intervalTimer = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        poll();
        startInterval();
      }
    };
    if (pollEnabled) {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    ctx.cleanup.push(() => {
      disposed = true;
      stopInterval();
      if (pulseTimer !== null) {
        clearTimeout(pulseTimer);
        pulseTimer = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    });

    // Push-Pfad: liveBus (PFS-397) wenn data-events-url gesetzt. Direkte
    // typisierte Subscription; Filter auf die eigene user-id IM WC —
    // liveBus bleibt generisch (fan-out an alle Subscriber des Event-
    // Namens, kein Domain-Routing im Bus). Kein initial-Fetcher: der
    // Initialwert kommt push-seitig (IV-prime Hub-Snapshot-on-Connect)
    // bzw. via Poll-Backup. Subscription bleibt offen bei hidden tab.
    if (eventsUrl) {
      const onDelta = (online: boolean) => (value: unknown) => {
        if (disposed) return;
        const rec = (value ?? {}) as Record<string, unknown>;
        if (String(rec[idField] ?? "") !== userId) return;
        setOnline(online);
      };
      ctx.cleanup.push(
        liveBus.track({
          url: eventsUrl,
          eventName: onlineEvent,
          key: userId,
          onValue: onDelta(true),
        }),
      );
      ctx.cleanup.push(
        liveBus.track({
          url: eventsUrl,
          eventName: offlineEvent,
          key: userId,
          onValue: onDelta(false),
        }),
      );
    }

    // Initial-Fetch + Polling (Fallback). Ohne data-events-url exakt
    // PFS-389-Verhalten; mit data-events-url nur wenn poll-interval > 0.
    if (pollEnabled) {
      poll();
      if (!document.hidden) startInterval();
    }
  },
});

function parsePollInterval(raw: string | null): number {
  if (raw === null) return DEFAULT_POLL_INTERVAL_MS;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0; // 0 = einmaliger Fetch
  return Math.max(n, MIN_POLL_INTERVAL_MS);
}
